import { Button, IconButton, Modal } from '@mui/material';
import RecordRTC from 'recordrtc';
import { storage } from 'firebaseConfig';
import { ref as fireRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import moment from 'moment';
import React, { useEffect, useRef, useState, useImperativeHandle, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { v4 } from 'uuid';
import Loading from "components/Loading";
import COLOR from 'utils/constants/color';
import { lighten } from 'polished';
import WaveformVisualizer from 'components/WaveformVisualizer';
import { Delete, PauseCircleFilledOutlined, PlayArrowOutlined, RecordVoiceOverOutlined } from '@mui/icons-material';
import {Mic} from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import TranscriptWithAudio from 'components/UI/transcriptWithAudio/transcriptWithAudio';
import NativeAudioPlayer, {
    getAudioExtensionFromMime,
    isIOSAudioDevice,
    normalizeAudioMimeType,
} from 'components/NativeAudioPlayer';
// Styled Components (same as before)
//  ----------------------------------------------------------------------------
const ModalContent = styled.div`
    align-items: center;
    background-color: ${COLOR.white};
    border-radius: 3px;
    display: inline-flex;
    flex-direction: column;
    left: 50%;
    max-width: 320px;
    padding: 16px;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
`;

const ModalRow = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-top: 16px;
    width: 100%;
`;

const Container = styled.div`
    padding: 16px;
    overflow: hidden;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const Counter = styled.span`
    color: ${COLOR.green100};
    display: block;
    font-size: 32px;
    font-weight: 600;
    line-height: 80px;
    margin-bottom: 16px;
    text-align: center;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0px;
    }
`;

const Text = styled.span`
    color: ${COLOR.black300};
    display: block;
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 24px;
    text-align: center;
`;

const Disclosure = styled.span`
    color: ${COLOR.yellow};
    display: block;
    font-size: 12px;
    line-height: 12px;
    margin-bottom: 10px;
    text-align: center;
`;

const Warning = styled.div`
    align-items: center;
    background-color: ${lighten(0.45, 'red')};
    border: 1px dashed red;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    justify-content: center;
    padding: 16px;
    text-align: center;
    width: 100%;
`;

const Wrapper = styled.div`
    border-bottom: 1px dashed ${COLOR.black700};
    margin-bottom: 8px;
    padding: 0px 0px 24px;
    width: 100%;
`;
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.6;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const RecordingIndicator = styled.div`
  width: 20px;
  height: 20px;
  background-color: red;
  border-radius: 50%;
  animation: ${pulse} 1s infinite;
  margin-bottom: 16px;
`;

const RecordButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(45deg, #9b4dca, #00c6ff); /* Updated gradient with purple to blue */
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.3s ease;
  border-radius:4px !important;
  min-height:40px;

  &:hover {
    background: linear-gradient(45deg, #00c6ff, #9b4dca); /* Reverse gradient on hover */
    transform: scale(1.05); /* Slightly enlarge the button on hover */
  }

  &:focus {
    outline: none;
  }

  svg {
    margin-right: 8px; /* Space between the icon and the text */
  }
`;
let recorder; // Global reference to RecordRTC instance

const AudioRecorder = React.forwardRef(({
    handleAudio,
    maxTime,
    message,
    refresh,
    storageLoc,
    isPreview = false,
    source,
    onRegisterMediaElement,
    onMediaPlay,
    wrapperStyle = {},
    hasSaveButton,
    doneButton,
    type = "",
    isMobileDevice=false,
    setButtonDisabled = (value) => { },
    isRecordingStatus = () => {},
    handleDeleteAudioVideo= () =>{},
    from="",
    disabled=false
}, ref) => {
    const [AllowAudio, setAllowAudio] = useState(false);
    const [ErrorAllowAudio, setErrorAllowAudio] = useState(false);
    const [audio, setAudio] = useState(source || null);
    const [counter, setCounter] = useState(maxTime || null);
    const [recording, setRecording] = useState(false);
    const [noAccess, setNoAccess] = useState(false);
    const [noPermission, setNoPermission] = useState(false);
    const [inUrlProcess, setInUrlProcess] = useState(false);
    const [paused, setPaused] = useState(false);
    const [stream, setStream] = useState(null);
    const timmerWorkerRef = useRef(null); 
    const saveButtonRef = useRef(null);
    const recordedMimeTypeRef = useRef('audio/webm');
    const audioElementRef = useRef(null);
    const recordingStartTimeRef = useRef(null);
    const syncIntervalRef = useRef(null);
    const isSavingRef = useRef(false);
    const pausedTimeRef = useRef(null);
    const totalPausedDurationRef = useRef(0);
    const studentReviewSubmission = (type === "student-submission-review") && isMobileDevice;

    const pauseAllMediaPlayback = () => {
        try {
            const mediaElements = document.querySelectorAll('audio, video');
            mediaElements.forEach((el) => {
                if (!el) return;
                if (typeof el.pause === 'function') {
                    el.pause();
                }
            });
        } catch (error) {
            Sentry.captureException(`506 Error pausing media playback: ${error}`);
        }
    };
    useEffect(() => {
        setCounter(maxTime);
    }, [maxTime]);

    useImperativeHandle(ref, () => ({
        pause: onPause,
        resume: onResume,
        stop: stopRecord,
        reset: resetRecording,
        start: startRecord,
        setAudio: setAudio
    }));

    useEffect(() => {
        if (source && source.length > 0) {
            setAudio(source);
            setRecording(false);
        }
    }, [source]);

    useEffect(() => {
        if (!audioElementRef.current) return;
        if (typeof onRegisterMediaElement !== "function") return;
        if (!audio) return;
        onRegisterMediaElement(audioElementRef.current);
    }, [audio, onRegisterMediaElement]);

    useEffect(() => {
        if (recording && !paused) {
            isRecordingStatus(true); 
        } else {
            isRecordingStatus(false); 
        }
    }, [recording, paused]);

    useEffect(() => {
        try {
            if (isPreview) {
                setNoAccess(false);
                setNoPermission(false);
                return;
            }
            const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
            if (navigator.permissions && !isFirefox) {
                navigator.permissions.query({ name: 'microphone' }).then(permissionStatus => {
                    if (permissionStatus.state === 'denied') {
                        setNoAccess(false);
                        setNoPermission(true);
                    }
                    permissionStatus.onchange = function () {
                        if (this.state === 'denied') {
                            setNoAccess(false);
                            setNoPermission(true);
                        } else {
                            setNoAccess(false);
                            setNoPermission(false);
                        }
                    };
                });
            }
            if (navigator?.mediaDevices && !isFirefox) {
                navigator.mediaDevices.getUserMedia({ audio: true })
                    .then((stream) => {
                        stream.getTracks().forEach(_tr => _tr.stop());
                        setNoAccess(false);
                        setNoPermission(false);
                    })
                    .catch(() => {
                        setNoAccess(true);
                        setNoPermission(false);
                    });
            }
        } catch (error) {
            Sentry.captureException(`507 Error in getUserMedia: ${error}`);
            setNoPermission(true);
        }
    }, []);

    const startRecord = () => {
        pauseAllMediaPlayback();
        if(setButtonDisabled){
            setButtonDisabled(true);
        }
        if (!recording && !isPreview) {
            setAllowAudio(true);
            const audioConstraints = {
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                    sampleRate: 44100
                }
            };
            
            navigator.mediaDevices.getUserMedia(audioConstraints)
                .then((stream) => {
                    const audioTracks = stream.getAudioTracks();
                    if (audioTracks.length === 0) {
                        throw new Error('No audio tracks found in stream');
                    }
                    
                    const audioTrack = audioTracks[0];
                    if (audioTrack.readyState !== 'live') {
                        throw new Error('Audio track is not live');
                    }
                    
                    setAllowAudio(false);
                    setStream(stream);
                    
                    let mimeType = 'audio/webm;codecs=opus';
                    let recorderType = RecordRTC.MediaStreamRecorder;
                    let recorderConfig = {
                        type: 'audio',
                        mimeType: mimeType,
                        recorderType: recorderType,
                        audioBitsPerSecond: 128000,
                        timeSlice: 1000
                    };

                    if (true) {
                        if (MediaRecorder.isTypeSupported('audio/mp4')) {
                            mimeType = 'audio/mp4';
                            recorderConfig.mimeType = mimeType;
                        } else if (MediaRecorder.isTypeSupported('audio/aac')) {
                            mimeType = 'audio/aac';
                            recorderConfig.mimeType = mimeType;
                        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
                            mimeType = 'audio/wav';
                            recorderType = RecordRTC.StereoAudioRecorder;
                            recorderConfig = {
                                type: 'audio',
                                mimeType: mimeType,
                                recorderType: recorderType,
                                numberOfAudioChannels: 1,
                                desiredSampRate: 44100,
                                audioBitsPerSecond: 128000,
                                timeSlice: 1000
                            };
                        }
                    } else if (!MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                        if (MediaRecorder.isTypeSupported('audio/mp4')) {
                            mimeType = 'audio/mp4';
                            recorderConfig.mimeType = mimeType;
                        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                            mimeType = 'audio/webm';
                            recorderConfig.mimeType = mimeType;
                        } else {
                            mimeType = 'audio/wav';
                            recorderType = RecordRTC.StereoAudioRecorder;
                            recorderConfig = {
                                type: 'audio',
                                mimeType: mimeType,
                                recorderType: recorderType,
                                numberOfAudioChannels: 1,
                                desiredSampRate: 44100,
                                audioBitsPerSecond: 128000,
                                timeSlice: 1000
                            };
                        }
                    }
                    recordedMimeTypeRef.current = mimeType;
                    recorder = new RecordRTC(stream, recorderConfig);
                    recorder.startRecording();
                    setRecording(true);
                    setPaused(false);
                    isSavingRef.current = false;
                    recordingStartTimeRef.current = Date.now();
                    pausedTimeRef.current = null;
                    totalPausedDurationRef.current = 0;
                    setCounter(maxTime);
                    timmerWorkerRef.current.postMessage({ command: 'start', time: maxTime, startTime: recordingStartTimeRef.current }); 
                })
                .catch((err) => {
                    Sentry.captureException(`508 Error in startRecord: ${err}`);
                    if(setButtonDisabled){
                        setButtonDisabled(false);
                    }
                    setAllowAudio(false);
                    setErrorAllowAudio(true);
                    if (stream) {
                        stream.getTracks().forEach(track => track.stop());
                    }
                });
        }
    };

    const stopRecord = async () => {
        if (recording && !isPreview) {
            timmerWorkerRef.current.postMessage({ command: 'stop' });
            
            const calculatedDuration = recordingStartTimeRef.current 
                ? ((Date.now() - recordingStartTimeRef.current - totalPausedDurationRef.current) / 1000)
                : null;
            
            recordingStartTimeRef.current = null;
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
            setInUrlProcess(true);
            const timeoutId = setTimeout(() => {
                setInUrlProcess(false);
                setRecording(false);
                isSavingRef.current = false;
                if(setButtonDisabled){
                    setButtonDisabled(false);
                }
            }, 10000);
            try {
                await new Promise((resolve, reject) => {
                    recorder.stopRecording(async () => {
                        const audioBlob = recorder.getBlob();
                        
                        if (!audioBlob || audioBlob.size === 0) {
                            clearTimeout(timeoutId);
                            reject(new Error('Empty audio blob'));
                            return;
                        }
                        
                        const blobUrl = URL.createObjectURL(audioBlob);
                        const audioElement = new Audio(blobUrl);
                        audioElement.preload = 'metadata';
                        
                        let metadataLoaded = false;
                        let durationTimeout = null;
                        
                        const processAudio = async (duration) => {
                            if (metadataLoaded) return;
                            metadataLoaded = true;
                            
                            if (durationTimeout) {
                                clearTimeout(durationTimeout);
                            }
                            
                            const finalDuration = (!duration || isNaN(duration) || duration === 0) 
                                ? (calculatedDuration || 0)
                                : duration;
                            if (finalDuration <= 0) {
                                clearTimeout(timeoutId);
                                reject(new Error('Invalid audio duration'));
                                return;
                            }
                            
                            const newBlob = new Blob([audioBlob], {
                                type: normalizeAudioMimeType(
                                    audioBlob.type || recordedMimeTypeRef.current,
                                ) || recordedMimeTypeRef.current,
                            });
                            const newBlobUrl = URL.createObjectURL(newBlob);
                            if (stream) {
                                stream.getTracks().forEach(track => track.stop());
                            }
                            setAudio(newBlobUrl);
                            URL.revokeObjectURL(blobUrl);
                            await uploadToStorage(newBlob);
                            clearTimeout(timeoutId);
                            resolve(newBlob);
                        };
                        
                        audioElement.addEventListener('loadedmetadata', async () => {
                            await processAudio(audioElement.duration);
                        });
                        
                        audioElement.addEventListener('durationchange', async () => {
                            if (audioElement.duration && !isNaN(audioElement.duration) && audioElement.duration > 0) {
                                await processAudio(audioElement.duration);
                            }
                        });
                        
                        audioElement.addEventListener('error', (e) => {
                            if (durationTimeout) {
                                clearTimeout(durationTimeout);
                            }
                            if (!metadataLoaded) {
                                if (calculatedDuration && calculatedDuration > 0) {
                                    processAudio(calculatedDuration).catch(() => {
                                        clearTimeout(timeoutId);
                                        reject(new Error('Failed to process audio'));
                                    });
                                } else {
                                    clearTimeout(timeoutId);
                                    console.log("Failed to load audio metadata");
                                    reject(new Error('Failed to load audio metadata'));
                                }
                            }
                        });
                        
                        audioElement.load();
                        
                        durationTimeout = setTimeout(async () => {
                            if (!metadataLoaded) {
                                if (calculatedDuration && calculatedDuration > 0) {
                                    await processAudio(calculatedDuration);
                                } else {
                                    clearTimeout(timeoutId);
                                    reject(new Error('Timeout loading audio metadata'));
                                }
                            }
                        }, 2000);
                    });
                });
                if(setButtonDisabled){
                    setButtonDisabled(false);
                }
            } catch (error) {
                Sentry.captureException(`509 Error in stopRecord: ${error}`);
                clearTimeout(timeoutId);
                setRecording(false);
                setInUrlProcess(false);
                if(setButtonDisabled){
                    setButtonDisabled(false);
                }
            }
            clearTimeout(timeoutId);
            setInUrlProcess(false);
            setRecording(false);
            isSavingRef.current = false;
            if(setButtonDisabled){
                setButtonDisabled(false);
            }
        }
    };

    const onPause = () => {
        if (recording && !paused) {
            recorder.pauseRecording();
            setPaused(true);
            pausedTimeRef.current = Date.now();
            timmerWorkerRef.current.postMessage({ command: 'stop' });
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
        }
    };

    const onResume = () => {
        pauseAllMediaPlayback();
        if(setButtonDisabled){
            setButtonDisabled(true);
        }
        if (recording && paused && pausedTimeRef.current) {
            const pauseDuration = Date.now() - pausedTimeRef.current;
            totalPausedDurationRef.current += pauseDuration;
            pausedTimeRef.current = null;
            
            recorder.resumeRecording();
            setPaused(false);
            
            const adjustedStartTime = recordingStartTimeRef.current + totalPausedDurationRef.current;
            timmerWorkerRef.current.postMessage({ command: 'start', time: maxTime, startTime: adjustedStartTime });
        }
    };

    useEffect(() => {
        const handleExternalMediaPlay = (event) => {
            const target = event?.target;
            if (!target) return;

            if (!(target instanceof HTMLMediaElement)) return;

            if (recording && !paused) {
                onPause();
            }
        };

        document.addEventListener('play', handleExternalMediaPlay, true);

        return () => {
            document.removeEventListener('play', handleExternalMediaPlay, true);
        };
    }, [recording, paused]);

    const resetRecording = (autoStart = false) => {
        if(setButtonDisabled){
            setButtonDisabled(false);
        }
        setCounter(null);
        setAudio(null);
        setRecording(false);
        setPaused(false);
        recordingStartTimeRef.current = null;
        pausedTimeRef.current = null;
        totalPausedDurationRef.current = 0;
        isSavingRef.current = false;
        if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
            syncIntervalRef.current = null;
        }
        timmerWorkerRef.current.postMessage({ command: 'reset' });
        if(autoStart){
            setTimeout(() => {
                startRecord();
            }, 100);
        }
    };

    useEffect(() => {
        timmerWorkerRef.current =  new Worker(`${(import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')}timeWorkerAudio.js?t=9`);
        timmerWorkerRef.current.onmessage = (event) => {
            const { type, countdownTime } = event.data;
            if (type === 'tick') {
                setCounter(countdownTime); 
            } else if (type === 'done') {
                setCounter(0);
                if (recording && !isSavingRef.current) {
                    isSavingRef.current = true;
                    if (syncIntervalRef.current) {
                        clearInterval(syncIntervalRef.current);
                        syncIntervalRef.current = null;
                    }
                    if (timmerWorkerRef.current) {
                        timmerWorkerRef.current.postMessage({ command: 'stop' });
                    }
                    stopRecord();
                }
            } else if (type === 'reset') {
                setCounter(maxTime);  
            }
        };
        return () => {
            if (timmerWorkerRef.current) {
                timmerWorkerRef.current.terminate();
            }
        };
    }, [maxTime]);

    useEffect(() => {
        const checkAndSyncTimer = () => {
            if (recording && !paused && recordingStartTimeRef.current && maxTime && !isSavingRef.current) {
                const currentPausedTime = pausedTimeRef.current ? (Date.now() - pausedTimeRef.current) : 0;
                const totalElapsed = Date.now() - recordingStartTimeRef.current - totalPausedDurationRef.current - currentPausedTime;
                const elapsedSeconds = Math.floor(totalElapsed / 1000);
                const remainingTime = Math.max(0, maxTime - elapsedSeconds);
                
                if (remainingTime <= 0) {
                    if (!isSavingRef.current && recording) {
                        isSavingRef.current = true;
                        setCounter(0);
                        if (syncIntervalRef.current) {
                            clearInterval(syncIntervalRef.current);
                            syncIntervalRef.current = null;
                        }
                        if (timmerWorkerRef.current) {
                            timmerWorkerRef.current.postMessage({ command: 'stop' });
                        }
                        stopRecord();
                    }
                } else {
                    if (timmerWorkerRef.current) {
                        timmerWorkerRef.current.postMessage({ command: 'check' });
                    }
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                if (recording && !paused && recordingStartTimeRef.current && maxTime) {
                    const currentPausedTime = pausedTimeRef.current ? (Date.now() - pausedTimeRef.current) : 0;
                    const totalElapsed = Date.now() - recordingStartTimeRef.current - totalPausedDurationRef.current - currentPausedTime;
                    const elapsedSeconds = Math.floor(totalElapsed / 1000);
                    const remainingTime = Math.max(0, maxTime - elapsedSeconds);
                    
                    if (remainingTime <= 0 && !isSavingRef.current) {
                        isSavingRef.current = true;
                        setCounter(0);
                        if (syncIntervalRef.current) {
                            clearInterval(syncIntervalRef.current);
                            syncIntervalRef.current = null;
                        }
                        if (timmerWorkerRef.current) {
                            timmerWorkerRef.current.postMessage({ command: 'stop' });
                        }
                        stopRecord();
                    } else if (timmerWorkerRef.current) {
                        timmerWorkerRef.current.postMessage({ command: 'check' });
                    }
                }
            }
        };

        const handleFocus = () => {
            if (recording && !paused && recordingStartTimeRef.current && maxTime) {
                const currentPausedTime = pausedTimeRef.current ? (Date.now() - pausedTimeRef.current) : 0;
                const totalElapsed = Date.now() - recordingStartTimeRef.current - totalPausedDurationRef.current - currentPausedTime;
                const elapsedSeconds = Math.floor(totalElapsed / 1000);
                const remainingTime = Math.max(0, maxTime - elapsedSeconds);
                
                if (remainingTime <= 0 && !isSavingRef.current) {
                    isSavingRef.current = true;
                    setCounter(0);
                    if (syncIntervalRef.current) {
                        clearInterval(syncIntervalRef.current);
                        syncIntervalRef.current = null;
                    }
                    if (timmerWorkerRef.current) {
                        timmerWorkerRef.current.postMessage({ command: 'stop' });
                    }
                    stopRecord();
                } else if (timmerWorkerRef.current) {
                    timmerWorkerRef.current.postMessage({ command: 'check' });
                }
            }
        };

        const handleBlur = () => {
            if (recording && !paused && timmerWorkerRef.current) {
                timmerWorkerRef.current.postMessage({ command: 'check' });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        if (recording && !paused) {
            syncIntervalRef.current = setInterval(() => {
                checkAndSyncTimer();
            }, 1000);
        }

        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, [recording, paused, maxTime]);
    const uploadToStorage = async (blob) => {
        if (blob) {
            const location = storageLoc || '/audio/activity-submissions';
            const id = `${moment().format('YYYY-MM-DD')}-${v4()}`;
            let contentType = normalizeAudioMimeType(blob.type || recordedMimeTypeRef.current)
                || (isIOSAudioDevice() ? 'audio/mp4' : 'audio/webm');
            if (isIOSAudioDevice() && contentType === 'audio/webm') {
                contentType = 'audio/mp4';
            }
            const extension = getAudioExtensionFromMime(contentType);
            const audioRef = fireRef(storage, `${location}/${id}${extension}`);
            const maxRetries = 3;
            let retryCount = 0;
            
             while (retryCount < maxRetries) {
                 try {
                     await uploadBytes(audioRef, blob, { contentType });
                     const url = await getDownloadURL(audioRef);
                     setData(url);
                     break; 
                 } catch (error) {
                     retryCount++;
                     
                     if (retryCount >= maxRetries) {
                         Sentry.captureException(`510 Error in uploadToStorage after ${maxRetries} retries: ${error}`);
                         console.error("Upload error:", error);
                         break;
                     } else {
                         const waitTime = Math.pow(2, retryCount) * 1000;
                         await new Promise(resolve => setTimeout(resolve, waitTime));
                     }
                 }
             }
        }
    };

    const setData = (url) => {
        setInUrlProcess(false);
        setAudio(url);
        handleAudio(url);
    };

    const secondsToTime = (secs, description = false) => {
        let hours = Math.floor(secs / (60 * 60));
        let divisor_for_minutes = secs % (60 * 60);
        let minutes = Math.floor(divisor_for_minutes / 60);
        let divisor_for_seconds = divisor_for_minutes % 60;
        let seconds = Math.ceil(divisor_for_seconds);
        let string = "";
        if (description) {
            if (hours > 0) {
                string += hours + " hours, ";
            }
            if (minutes > 0) {
                string += minutes + " minutes";
            }
            if (seconds > 0) {
                string += ", " + (seconds < 10 ? "0" : "") + seconds + " seconds";
            }
        } else {
            if (hours > 0) {
                string += hours + ":";
            }
            if (minutes > 0) {
                string += (minutes < 10 ? "0" : "") + minutes;
            } else {
                string += "00";
            }
            if (seconds > 0) {
                string += ":" + (seconds < 10 ? "0" : "") + seconds;
            } else {
                string += ":00";
            }
        }
        return string;
    };

    const handleDeleteAudio = () => {
        setCounter(null);
        setAudio(null);
        setRecording(false);
        setPaused(false);
        recordingStartTimeRef.current = null;
        pausedTimeRef.current = null;
        totalPausedDurationRef.current = 0;
        isSavingRef.current = false;
        if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
            syncIntervalRef.current = null;
        }
        timmerWorkerRef.current.postMessage({ command: 'reset' });
        if(handleDeleteAudioVideo){
            handleDeleteAudioVideo("audio");
        }
    }



    return (
        <Wrapper style={wrapperStyle}>
            <Modal open={AllowAudio} onClose={() => setAllowAudio(false)}>
                <ModalContent>
                    To record the audio, click “Allow” above to give access to your computer’s microphone.
                </ModalContent>
            </Modal>
            <Modal open={ErrorAllowAudio} onClose={() => setErrorAllowAudio(false)}>
                <ModalContent>
                    To record the audio, you need access to your computer's microphone. Click “Allow” to give access.
                    <ModalRow>
                        <Button color="primary" variant="contained" onClick={() => setErrorAllowAudio(false)}>
                            Ok, I got it!
                        </Button>
                    </ModalRow>
                </ModalContent>
            </Modal>
            <Container 
            style={{
                    flexDirection: studentReviewSubmission && !audio && "row",
                    justifyContent: studentReviewSubmission && "start",
                    alignItems: studentReviewSubmission && "center",
                    gap: studentReviewSubmission && !audio && "10px",
                    width: studentReviewSubmission && "fit-content",
                    padding: studentReviewSubmission && audio ?  "0px" : studentReviewSubmission && !audio  ? "12px": "16px",
                    marginTop: studentReviewSubmission && audio && "10px",
                    height: studentReviewSubmission && !audio && "66px",
                    borderRadius:  studentReviewSubmission && "10px"
                    
                }}
            className="canvaWidthSet"
            >
                 {(noAccess || noPermission) && !audio  ? (
                    <Warning className={`${studentReviewSubmission ? "mic-permission-for-review mic-permission-student mic-permission-text  mt-0px h-unset" : ""}`}>
                        {noPermission ? 'Please grant access to your microphone to ensure proper recording functionality.' : ''}
                        {noAccess && !noPermission ? 'Please connect your microphone to ensure optimal recording functionality.' : ''}
                        {/* <Button variant='contained' onClick={refresh} style={{ backgroundColor: 'red', color: 'white', marginTop: '16px', width: '100%' }}>Refresh</Button> */}
                    </Warning>
                ) : (
                    <>
                     {stream && recording && !studentReviewSubmission && <WaveformVisualizer stream={stream} isRecording={recording} paused={paused} />}
                        {(!recording && audio) ? (
                            <>
                                    <Row style={{width:'100%'}}>
                                    <TranscriptWithAudio audioUrl={audio} title={audio}>
                                    <NativeAudioPlayer
                                        url={audio}
                                        className={studentReviewSubmission ? 'audio-player-student-review' : ''}
                                        onRef={(el) => { audioElementRef.current = el; }}
                                        onPlay={(el) => {
                                            if (typeof onMediaPlay === 'function' && el) {
                                                onMediaPlay(el);
                                            }
                                        }}
                                        style={{
                                            height: studentReviewSubmission ? '27px' : '40px',
                                        }}
                                    />
                                    </TranscriptWithAudio>
                                        
                                    
                                    </Row>
                                    <Row style={{ justifyContent: 'space-between' }}>
                                        <div className="flex items-center">
                                            <RecordButton className='reRecordBtn' onClick={() => resetRecording(true)} variant="contained"
                                                    color="primary" style={{ marginLeft: '8px' }}><Mic />Re-record</RecordButton>

                                                {from === "worksheet" && (
                                                        <IconButton onClick={() => {
                                                            handleDeleteAudio()
                                                        }}>
                                                        <Delete color='error' />
                                                        </IconButton>
                                                )}
                                           
                                        </div>
                                           
                                        {doneButton ? doneButton : null}
                                    </Row>
                            </>
                        ) : (
                            <>
                                {counter === null && (
                                    <>
                                        <Text style={{ marginBottom: '5px' }}>{message ?? `Click "Record" below. You will have ${secondsToTime(maxTime, true)} to record your answer.`}</Text>
                                        {!studentReviewSubmission && <Disclosure>Place your microphone close for better audio quality.</Disclosure> }
                                        
                                    </>
                                )}
                                {(maxTime && counter > 0) && (
                                    <>
                                        {!studentReviewSubmission && <Disclosure>Place your microphone close for better audio quality.</Disclosure>}
                                        <Counter className={`${studentReviewSubmission ? "m-none ft-17px" : ""}`}>{secondsToTime(counter)}</Counter>
                                    </>
                                )}
                                <Row className={`${studentReviewSubmission ? "m-none mb-16px items-center" : ""}`}>
                                    {studentReviewSubmission ?
                                     <button
                                     disabled={inUrlProcess || isPreview || inUrlProcess || disabled}
                                     color="primary"
                                     variant='contained'
                                     className="record-button-student-review items-center flex"
                                     onClick={() => {
                                         if (!isPreview) {
                                             if (recording && paused) {
                                                 onResume();
                                             } else if (recording && !paused) {
                                                 onPause();
                                             } else {
                                                 startRecord();
                                             }
                                         }
                                     }}
                                     style={{ marginRight: '8px' }}
                                 >
                                     {recording && paused ? <PlayArrowOutlined style={{color: "#03989e"}} /> :recording && !paused  ? <PauseCircleFilledOutlined style={{color:  "#03989e"}} /> : <RecordVoiceOverOutlined style={{color: "#03989e"}} />}
                                     {/* {recording && paused ? "Resume" : recording && !paused ? "Pause" : "Record"} */}
                                 </button>
                                    
                                    : 
                                    
                                    <Button
                                    disabled={inUrlProcess || isPreview || inUrlProcess || disabled}
                                    color="primary"
                                    variant='contained'
                                    onClick={() => {
                                        if (!isPreview) {
                                            if (recording && paused) {
                                                onResume();
                                            } else if (recording && !paused) {
                                                onPause();
                                            } else {
                                                startRecord();
                                            }
                                        }
                                    }}
                                    style={{ marginRight: '8px' }}
                                >
                                    {recording && paused ? "Resume" : recording && !paused ? "Pause" : "Record"}
                                </Button>
                                    }
                                   
                                    <Button style={{height: studentReviewSubmission ? "31px" : ""}} variant="outlined" disabled={!recording || (maxTime && maxTime === counter) || inUrlProcess || disabled} ref={saveButtonRef} onClick={() => stopRecord()}>
                                        {inUrlProcess && <Loading  size={14} />}
                                        {hasSaveButton ? "Save" : "Stop"}
                                    </Button>
                                   
                                </Row>
                            </>
                        )}
                    </>
                )}
            </Container>
        </Wrapper>
    );
});

export default AudioRecorder;